/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import type { DesktopRouteState } from '@poweralpha/common';
import type { DesktopState } from './windowSystem';
import { type DesktopBounds, isTiledWindow } from './windowTiling';

type Rect = { x: number; y: number; width: number; height: number };

/** The rect slid (then shrunk, if it still doesn't fit) inside the bounds; the same object if it fits. */
const fitRect = <R extends Rect>(rect: R, { width, height }: DesktopBounds): R => {
  const fittedWidth = Math.min(rect.width, width);
  const fittedHeight = Math.min(rect.height, height);
  const x = Math.max(0, Math.min(rect.x, width - fittedWidth));
  const y = Math.max(0, Math.min(rect.y, height - fittedHeight));
  if (x === rect.x && y === rect.y && fittedWidth === rect.width && fittedHeight === rect.height)
    return rect;
  return { ...rect, x, y, width: fittedWidth, height: fittedHeight };
};

/**
 * Keeps windows inside the desktop when it narrows (the Agent column opening or growing): a
 * window slides left, and shrinks only when it no longer fits. Every size a window can come back
 * to is fitted, so nothing reopens or restores under the Agent: closed windows, the windowed size
 * a maximized window keeps underneath, and the size a tiled window returns to (`restoreBounds`).
 * Tiled frames themselves follow the bounds through the tiling.
 */
export function fitWindowsToBounds<T extends DesktopRouteState>(
  state: DesktopState<T>,
  bounds: DesktopBounds,
): DesktopState<T> {
  let changed = false;
  const windows = state.windows.map((window) => {
    const tiled = isTiledWindow(state, window.id);
    const metrics = tiled ? window : fitRect(window, bounds);
    const restoreBounds = window.restoreBounds && fitRect(window.restoreBounds, bounds);
    if (metrics === window && restoreBounds === window.restoreBounds) return window;
    changed = true;
    return { ...metrics, restoreBounds };
  });
  return changed ? { ...state, windows } : state;
}
